import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Upload, Search, Trash2, FileText, Download, Loader2, RefreshCw, Users, Mail } from 'lucide-react';
import api from '../lib/api';
import { fmtDateTime } from '../lib/dateUtils';
import { useAuth } from '../contexts/AuthContext';

const CATEGORIES = {
  formazione: { label: 'Formazione', color: 'bg-blue-50 text-blue-700 border-blue-300' },
  idoneita: { label: 'Idoneità', color: 'bg-emerald-50 text-emerald-700 border-emerald-300' },
};

const fmtSize = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export default function EmailArchivePage() {
  const { isRole } = useAuth();
  const isAdmin = isRole('admin');
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');
  const [q, setQ] = useState('');
  const [employeeId, setEmployeeId] = useState('all');
  const [employees, setEmployees] = useState([]);
  const [status, setStatus] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [msg, setMsg] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadCat, setUploadCat] = useState('formazione');
  const [assignDoc, setAssignDoc] = useState(null);
  const [assignIds, setAssignIds] = useState([]);
  const [assignSearch, setAssignSearch] = useState('');
  const fileRef = useRef(null);
  const [mail, setMail] = useState(null);
  const [showMail, setShowMail] = useState(false);
  const [token, setToken] = useState('');
  const [copied, setCopied] = useState(false);
  const [webappUrl, setWebappUrl] = useState('');
  const [webappMsg, setWebappMsg] = useState(null);

  const loadMail = useCallback(async () => {
    if (!isAdmin) return;
    try { setMail((await api.get('/email-archive/mail-settings')).data); } catch { /* ignora */ }
  }, [isAdmin]);
  useEffect(() => { loadMail(); }, [loadMail]);

  const openMail = async () => {
    setCopied(false);
    setWebappUrl(mail?.webapp_url || '');
    setWebappMsg(null);
    setShowMail(true);
    try { setToken((await api.post('/email-archive/mail-settings/token', {})).data.token); } catch { setToken(''); }
  };

  const rotateToken = async () => {
    if (!window.confirm('Generare una nuova chiave? Lo script gia installato smettera di funzionare finche non incolli quello nuovo.')) return;
    setToken((await api.post('/email-archive/mail-settings/token', { rotate: true })).data.token);
    setCopied(false);
    loadMail();
  };

  const saveWebapp = async () => {
    setWebappMsg(null);
    try {
      await api.put('/email-archive/mail-settings/webapp', { url: webappUrl });
      setWebappMsg({ ok: true, text: webappUrl ? 'Indirizzo salvato. Ora il pulsante "Aggiorna da email" funziona.' : 'Indirizzo rimosso.' });
      loadMail();
    } catch (err) {
      setWebappMsg({ ok: false, text: err.response?.data?.detail || 'Salvataggio non riuscito' });
    }
  };

  const scriptText = `const BASE_GESTIONALE = '${window.location.origin}/api';
const CHIAVE = '${token}';

// Archivio PDF: legge le email di questi mittenti e invia al gestionale i PDF allegati (anche dentro file ZIP).
// "iniziale" e' la data da cui recuperare lo storico (la prima volta legge tutto); poi lo script legge solo le email nuove.
const FLUSSI = [
  { mittente: 'formazione@mpmonline.it', percorso: '/email-archive/inbound/formazione', ricerca: 'has:attachment (filename:pdf OR filename:zip)', iniziale: '2015-01-01', giorni: 365 },
  { mittente: 'segreteria@studiobcz.com', percorso: '/email-archive/inbound/idoneita', ricerca: 'has:attachment (filename:pdf OR filename:zip)', iniziale: '2015-01-01', giorni: 365 }
];

function importaArchivio() {
  const lock = LockService.getScriptLock();
  if (!lock.tryLock(5000)) return;
  try { esegui_(); } finally { lock.releaseLock(); }
}

function doGet(e) {
  if (!e || !e.parameter || e.parameter.k !== CHIAVE) return ContentService.createTextOutput('chiave non valida');
  importaArchivio();
  return ContentService.createTextOutput('ok');
}

function chiama_(percorso, opzioni) {
  opzioni.headers = { 'X-Inbound-Token': CHIAVE };
  opzioni.muteHttpExceptions = true;
  return UrlFetchApp.fetch(BASE_GESTIONALE + percorso, opzioni);
}

function esegui_() {
  const inizio = Date.now();
  FLUSSI.forEach(function (f, i) {
    try {
      let ancora = importaFlusso_(f, i, inizio);
      while (ancora && Date.now() - inizio < 240000) ancora = importaFlusso_(f, i, inizio);
    } catch (e) {
      console.error('Flusso ' + f.mittente + ': ' + e);
    }
  });
}

// Legge le email a finestre di date (dalla piu' vecchia) e ricorda fino a dove e' arrivato:
// i giri successivi non rileggono la posta gia' elaborata. Restituisce true se c'e' ancora storico da leggere.
function importaFlusso_(f, indice, inizio) {
  const props = PropertiesService.getScriptProperties();
  const chiaveDesde = 'archivio_desde_' + indice;
  const adesso = Math.floor(Date.now() / 1000);
  let desde = Number(props.getProperty(chiaveDesde)) || Math.floor(new Date(f.iniziale + 'T00:00:00Z').getTime() / 1000);
  const giorni = f.giorni || 15;
  const inDiretta = desde + giorni * 86400 >= adesso;
  const fine = inDiretta ? adesso + 86400 : desde + giorni * 86400;

  const messaggi = [];
  let start = 0;
  while (true) {
    const threads = GmailApp.search('from:' + f.mittente + ' ' + f.ricerca + ' after:' + desde + ' before:' + fine, start, 100);
    threads.forEach(function (t) {
      t.getMessages().forEach(function (m) {
        if (m.getDate().getTime() / 1000 >= desde - 3600) messaggi.push(m);
      });
    });
    if (threads.length < 100) break;
    start += 100;
  }
  messaggi.sort(function (a, b) { return a.getDate() - b.getDate(); });

  let ultimoOk = desde;
  let completo = true;
  if (messaggi.length) {
    const ids = messaggi.map(function (m) { return m.getHeader('Message-ID') || m.getId(); });
    const ctrl = chiama_(f.percorso + '/check', { method: 'post', contentType: 'application/json', payload: JSON.stringify({ ids: ids }) });
    if (ctrl.getResponseCode() !== 200) {
      console.error('Errore ' + ctrl.getResponseCode() + ': ' + ctrl.getContentText());
      return false;
    }
    const nuovi = JSON.parse(ctrl.getContentText()).new;

    for (let i = 0; i < messaggi.length; i++) {
      const m = messaggi[i];
      if (Date.now() - inizio > 270000) { console.log('Tempo quasi scaduto, si riprende al prossimo giro'); completo = false; break; }
      if (nuovi.indexOf(ids[i]) !== -1) {
        const allegati = m.getAttachments().filter(function (a) { const n = a.getName().toLowerCase(); return n.endsWith('.pdf') || n.endsWith('.zip'); });
        if (allegati.length) {
          const payload = { message_id: ids[i], subject: m.getSubject(), from: m.getFrom(), date: m.getDate().toISOString() };
          allegati.forEach(function (p, k) { payload['file' + k] = p.copyBlob(); });
          const r = chiama_(f.percorso, { method: 'post', payload: payload });
          if (r.getResponseCode() !== 200) {
            console.error('Errore ' + r.getResponseCode() + ': ' + r.getContentText());
            completo = false;
            break;
          }
        }
      }
      ultimoOk = Math.floor(m.getDate().getTime() / 1000);
    }
  }
  if (completo) {
    props.setProperty(chiaveDesde, String(inDiretta ? adesso - 3600 : fine));
  } else {
    props.setProperty(chiaveDesde, String(Math.max(desde, ultimoOk - 60)));
  }
  return completo && !inDiretta;
}`;

  const copyScript = async () => {
    try { await navigator.clipboard.writeText(scriptText); setCopied(true); } catch { setCopied(false); }
  };

  const load = useCallback(async () => {
    try {
      const params = {};
      if (category !== 'all') params.category = category;
      if (q.trim()) params.q = q.trim();
      if (employeeId !== 'all') params.employee_id = employeeId;
      setDocs((await api.get('/email-archive', { params })).data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [category, q, employeeId]);

  const loadStatus = useCallback(async () => {
    try { setStatus((await api.get('/email-archive/status')).data); } catch { /* ignora */ }
  }, []);

  useEffect(() => {
    const t = setTimeout(load, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  useEffect(() => {
    loadStatus();
    api.get('/employees').then(r => setEmployees((r.data || []).filter(e => e.is_active !== false))).catch(() => {});
  }, [loadStatus]);

  const openFile = async (doc, download = false) => {
    try {
      const res = await api.get(`/email-archive/${doc.id}/file`, { params: { download }, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      if (download) {
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.filename.toLowerCase().endsWith('.pdf') ? doc.filename : `${doc.filename}.pdf`;
        a.click();
      } else {
        window.open(url, '_blank');
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      setMsg({ ok: false, text: 'Impossibile aprire il file' });
    }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Eliminare "${doc.filename}" dall'archivio?`)) return;
    await api.delete(`/email-archive/${doc.id}`);
    load();
    loadStatus();
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setUploading(true);
    setMsg(null);
    try {
      const fd = new FormData();
      fd.append('category', uploadCat);
      files.forEach(f => fd.append('files', f));
      const res = (await api.post('/email-archive/upload', fd)).data.results;
      const ok = res.filter(r => r.status === 'importato').length;
      const dup = res.filter(r => r.status === 'duplicato').length;
      const err = res.filter(r => r.status === 'errore' || r.status === 'ignorato').length;
      setMsg({ ok: !err, text: `${ok} caricati${dup ? `, ${dup} già presenti` : ''}${err ? `, ${err} non validi o ignorati (${res.filter(r => r.detail).map(r => `${r.file}: ${r.detail}`).slice(0, 3).join('; ')})` : ''}.` });
      load();
      loadStatus();
    } catch (err) {
      setMsg({ ok: false, text: err.response?.data?.detail || 'Caricamento non riuscito' });
    } finally {
      setUploading(false);
    }
  };

  const refreshFromMail = async () => {
    setMsg(null);
    try {
      const r = (await api.post('/email-archive/refresh', null, { params: { auto: false } })).data;
      if (!r.started) {
        setMsg(r.reason === 'not_configured'
          ? { ok: false, text: 'Aggiornamento non ancora collegato: l\'amministratore deve completare "Collega email" in questa pagina.' }
          : { ok: true, text: 'Aggiornamento già in corso o appena eseguito.' });
        return;
      }
      setRefreshing(true);
      let st = null;
      for (let i = 0; i < 40; i++) {
        await new Promise(res => setTimeout(res, 4000));
        st = (await api.get('/email-archive/refresh/status')).data;
        if (!st.running) break;
      }
      await load();
      loadStatus();
      if (st?.last_refresh_status === 'errore') setMsg({ ok: false, text: st.last_refresh_error || 'Aggiornamento non riuscito' });
      else setMsg({ ok: true, text: 'Archivio aggiornato dalle caselle email.' });
    } catch (err) {
      setMsg({ ok: false, text: err.response?.data?.detail || 'Aggiornamento non riuscito' });
    } finally {
      setRefreshing(false);
    }
  };

  const openAssign = (doc) => {
    setAssignDoc(doc);
    setAssignIds((doc.employees || []).map(e => e.id));
    setAssignSearch('');
  };

  const saveAssign = async () => {
    await api.put(`/email-archive/${assignDoc.id}`, { employee_ids: assignIds });
    setAssignDoc(null);
    load();
  };

  const filteredEmps = employees
    .filter(e => `${e.first_name} ${e.last_name}`.toLowerCase().includes(assignSearch.toLowerCase()))
    .sort((a, b) => `${a.last_name} ${a.first_name}`.localeCompare(`${b.last_name} ${b.first_name}`));

  const fmtRecv = (iso) => (iso ? fmtDateTime(iso) : 'mai');

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="email-archive-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Archivio formazione e idoneità</h1>
          <p className="text-sm text-slate-500">PDF ricevuti via email da MPM Online (formazione) e dallo Studio BCZ (giudizi di idoneità).</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isAdmin && (
            <Button variant="outline" onClick={openMail} data-testid="archive-mail-btn">
              <Mail size={16} className="mr-1" />Collega email
            </Button>
          )}
          <Button variant="outline" onClick={refreshFromMail} disabled={refreshing} data-testid="archive-refresh-btn">
            {refreshing ? <Loader2 size={16} className="mr-1 animate-spin" /> : <RefreshCw size={16} className="mr-1" />}
            {refreshing ? 'Aggiornamento...' : 'Aggiorna da email'}
          </Button>
          <Select value={uploadCat} onValueChange={setUploadCat}>
            <SelectTrigger className="w-40" data-testid="archive-upload-cat"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="formazione">Formazione</SelectItem>
              <SelectItem value="idoneita">Idoneità</SelectItem>
            </SelectContent>
          </Select>
          <input ref={fileRef} type="file" accept="application/pdf,.pdf,.zip,application/zip" multiple className="hidden" onChange={handleUpload} data-testid="archive-file-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="archive-upload-btn">
            {uploading ? <Loader2 size={16} className="mr-1 animate-spin" /> : <Upload size={16} className="mr-1" />}
            Carica PDF o ZIP
          </Button>
        </div>
      </div>

      {status && (
        <p className="text-xs text-slate-500" data-testid="archive-status">
          Formazione: {status.formazione.count} documenti (ultima email importata il {fmtRecv(status.formazione.last_received_at)}) ·{' '}
          Idoneità: {status.idoneita.count} documenti (ultima email importata il {fmtRecv(status.idoneita.last_received_at)})
        </p>
      )}
      {msg && <p className={`text-sm ${msg.ok ? 'text-green-700' : 'text-red-600'}`} data-testid="archive-msg">{msg.text}</p>}

      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9" placeholder="Cerca per nome file, oggetto, dipendente o testo del PDF" value={q} onChange={e => setQ(e.target.value)} data-testid="archive-search" />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-44" data-testid="archive-filter-cat"><SelectValue placeholder="Tipo" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i tipi</SelectItem>
            <SelectItem value="formazione">Formazione</SelectItem>
            <SelectItem value="idoneita">Idoneità</SelectItem>
          </SelectContent>
        </Select>
        <Select value={employeeId} onValueChange={setEmployeeId}>
          <SelectTrigger className="w-56" data-testid="archive-filter-emp"><SelectValue placeholder="Dipendente" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i dipendenti</SelectItem>
            {[...employees].sort((a, b) => `${a.last_name} ${a.first_name}`.localeCompare(`${b.last_name} ${b.first_name}`)).map(e => (
              <SelectItem key={e.id} value={e.id}>{e.last_name} {e.first_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ricevuto il</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>File</TableHead>
                <TableHead className="hidden md:table-cell">Dipendenti</TableHead>
                <TableHead className="w-40">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400">Caricamento...</TableCell></TableRow>
              ) : docs.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400">Nessun documento in archivio</TableCell></TableRow>
              ) : docs.map(d => (
                <TableRow key={d.id} className="hover:bg-slate-50" data-testid={`archive-row-${d.id}`}>
                  <TableCell className="text-sm whitespace-nowrap">{fmtDateTime(d.email_date)}</TableCell>
                  <TableCell><Badge variant="outline" className={CATEGORIES[d.category]?.color}>{CATEGORIES[d.category]?.label || d.category}</Badge></TableCell>
                  <TableCell className="max-w-[360px]">
                    <button type="button" onClick={() => openFile(d)} className="flex items-center gap-1.5 text-sm font-medium text-blue-700 hover:underline text-left">
                      <FileText size={14} className="shrink-0" /><span className="truncate">{d.filename}</span>
                    </button>
                    <p className="text-xs text-slate-400 truncate">{d.subject ? `${d.subject} · ` : ''}{fmtSize(d.size)}</p>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <div className="flex flex-wrap gap-1">
                      {(d.employees || []).length === 0
                        ? <span className="text-xs text-slate-400">non riconosciuto</span>
                        : d.employees.map(e => <Badge key={e.id} variant="outline" className="text-xs">{e.name}</Badge>)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" className="p-1 h-auto" title="Scarica" onClick={() => openFile(d, true)} data-testid={`archive-dl-${d.id}`}><Download size={15} /></Button>
                      <Button variant="ghost" size="sm" className="p-1 h-auto" title="Assegna dipendenti" onClick={() => openAssign(d)} data-testid={`archive-assign-${d.id}`}><Users size={15} /></Button>
                      <Button variant="ghost" size="sm" className="p-1 h-auto text-red-600 hover:text-red-800" title="Elimina" onClick={() => remove(d)} data-testid={`archive-del-${d.id}`}><Trash2 size={15} /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={showMail} onOpenChange={setShowMail}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Importa i PDF dalle caselle email</DialogTitle></DialogHeader>
          <div className="space-y-4 text-sm">
            <p className="text-slate-500">
              Questo collegamento è <b>separato da quello degli Ordini Cliente</b>: ha il suo script e la sua chiave. Lo script va installato
              su due caselle: <b>info@metalcovering.org</b> (email di formazione@mpmonline.it) e <b>g.bagnato@metalcovering.org</b> (email di
              segreteria@studiobcz.com). I PDF allegati dentro file ZIP vengono estratti e archiviati uno per uno. La prima volta legge tutte le email passate, poi solo le nuove. Le email non vengono spostate né segnate come lette.
            </p>
            <ol className="list-decimal pl-5 space-y-2">
              <li>Accedi a Google con l'account della casella e apri <a className="text-blue-600 underline" href="https://script.google.com" target="_blank" rel="noopener noreferrer">script.google.com</a>, poi <b>Nuovo progetto</b> (un progetto nuovo, diverso da quello degli ordini). Ripeti con la seconda casella.</li>
              <li>Cancella il codice presente e incolla il codice del riquadro in fondo (pulsante Copia). Premi l'icona del dischetto per salvare.</li>
              <li>In alto scegli la funzione <b>importaArchivio</b> e premi <b>Esegui</b>. Google chiede il permesso di leggere la posta e di collegarsi a un servizio esterno: autorizza (se compare "app non verificata", scegli Avanzate e Vai a progetto).</li>
              <li>A sinistra apri <b>Attivatori</b> (icona dell'orologio), poi <b>Aggiungi attivatore</b>: funzione <b>importaArchivio</b>, origine evento <b>Basato sul tempo</b>, <b>Timer in minuti</b>, ogni <b>10 minuti</b>. Salva.</li>
              <li><b>Per il pulsante "Aggiorna da email"</b> (consigliato): in alto a destra premi <b>Distribuisci</b>, poi <b>Nuova distribuzione</b>, ingranaggio, <b>App web</b>. Imposta <b>Esegui come: Me</b> e <b>Chi ha accesso: Chiunque</b>, premi <b>Distribuisci</b>, autorizza se richiesto e copia l'<b>URL dell'app web</b> (finisce con /exec). Incollalo qui sotto.</li>
            </ol>
            <div className="space-y-1.5">
              <Label>URL dell'app web (uno per riga, uno per ogni casella dove hai installato lo script)</Label>
              <div className="flex gap-2">
                <Textarea rows={3} placeholder="https://script.google.com/macros/s/.../exec" value={webappUrl} onChange={e => setWebappUrl(e.target.value)} data-testid="archive-webapp-url" />
                <Button onClick={saveWebapp} data-testid="archive-webapp-save">Salva</Button>
              </div>
              {webappMsg && <p className={`text-xs ${webappMsg.ok ? 'text-green-700' : 'text-red-600'}`}>{webappMsg.text}</p>}
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>Codice da incollare (uguale per le due caselle)</Label>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={rotateToken} data-testid="archive-rotate-btn">Nuova chiave</Button>
                  <Button size="sm" onClick={copyScript} disabled={!token} data-testid="archive-copy-btn">{copied ? 'Copiato' : 'Copia'}</Button>
                </div>
              </div>
              <textarea readOnly rows={9} value={token ? scriptText : 'Generazione della chiave...'} className="w-full rounded-md border border-slate-200 bg-slate-50 p-2 font-mono text-[11px]" onFocus={e => e.target.select()} data-testid="archive-script" />
              <p className="text-xs text-slate-400">Il codice contiene la chiave segreta: non condividerlo. Con molte email vecchie la prima esecuzione può richiedere più giri da 10 minuti: riprende da sola da dove si era fermata.</p>
            </div>
            {mail?.last_check_at && (
              <p className="text-green-700 text-xs">Collegamento attivo: ultimo controllo dello script il {fmtDateTime(mail.last_check_at)}.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { loadMail(); setShowMail(false); }}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!assignDoc} onOpenChange={() => setAssignDoc(null)}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Dipendenti collegati al documento</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 truncate">{assignDoc?.filename}</p>
          <Input placeholder="Cerca dipendente" value={assignSearch} onChange={e => setAssignSearch(e.target.value)} />
          <div className="max-h-72 overflow-y-auto border rounded-md divide-y">
            {filteredEmps.map(e => (
              <label key={e.id} className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-slate-50">
                <input type="checkbox" checked={assignIds.includes(e.id)}
                  onChange={() => setAssignIds(p => (p.includes(e.id) ? p.filter(x => x !== e.id) : [...p, e.id]))} />
                {e.last_name} {e.first_name}
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDoc(null)}>Annulla</Button>
            <Button onClick={saveAssign} data-testid="archive-assign-save">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
